import {useRef} from "react";
import {DraftLine} from "./folioDraft.util";
import {isTipLine} from "./folioLine.util";

/** The lines that arrived from the server, and what the last of them changes in the totals. */
export interface LineArrivals {
    /** Their keys: each line is highlighted once, as it arrives. */
    keys: ReadonlySet<string>
    /** Counts the arrivals, so the totals light up again with the next one. */
    count: number
    /** The last arrival brought a tip line: Tip and Total change. */
    tip: boolean
    /** The last arrival brought a charged line: Subtotal, VAT and Total change. */
    charges: boolean
}

const NONE: LineArrivals = {keys: new Set(), count: 0, tip: false, charges: false};

/**
 * The lines that arrive from the server while the folio's lines show - the tip line a guest
 * confirmation posts, a line added elsewhere -, to highlight them. Neither the lines the folio opened
 * with nor the ones staff add here count: a new line takes the focus, and saved it comes back with
 * its id during the save - which is how it is told apart. The folio view is keyed by the folio, so
 * this starts over with each one.
 */
export const useLineArrivals = (lines: DraftLine[], ready: boolean, saving: boolean): LineArrivals => {
    const seen = useRef<Set<string>>();
    const arrivals = useRef(NONE);
    // The save's lines come back in the render after its last one with `saving`
    const wasSaving = useRef(saving);

    if (ready) {
        if (!seen.current) {
            seen.current = new Set(lines.map(line => line.key));
        } else {
            const known = seen.current;
            const added = lines.filter(line => !known.has(line.key));
            added.forEach(line => known.add(line.key));
            const arrived = saving || wasSaving.current ? [] : added.filter(line => !!line.item.id);
            if (arrived.length) {
                arrivals.current = {
                    keys: new Set([...Array.from(arrivals.current.keys), ...arrived.map(line => line.key)]),
                    count: arrivals.current.count + 1,
                    tip: arrived.some(line => isTipLine(line.item)),
                    charges: arrived.some(line => !isTipLine(line.item)),
                };
            }
        }
    }
    wasSaving.current = saving;
    return arrivals.current;
};
